import {
  icDphMatchesDic,
  isValidDic,
  isValidIcDph,
  isValidIco,
  tt,
} from '@agos/shared'

/**
 * One franchise, and the two things a super-admin does from its page: jump into its manager, or take it
 * out of service.
 *
 * **The jump needs an active `main_admin`, and two of the three franchises have none.**
 * `ManagerSsoService.issueToken` mints a token for the franchise's first active `main_admin` and throws
 * 404 when there is not one — *"Žiadny aktívny main_admin pre túto franchise. Najprv vytvorte admin
 * účet."* Measured: all **8** administrators on the platform belong to franchise 1 (2 `main_admin`,
 * 6 `admin`), so Nesvady and Sereď have nobody and the jump fails for both. Legacy discovers that with
 * an `alert()` after the round trip; this knows before the click, from `GET /super-admin/admins`.
 *
 * **The shared manager domain is not a bug.** All three franchises point at `manager.pizzamajstro.sk`,
 * so a jump into Nesvady lands on Šurany's host with a token claiming franchise 7. `TenantResolutionFilter`
 * resolves that correctly — when the claim and the Host disagree but share a `manager_group`, the claim
 * wins, and all three are in group `PizzaMajstro`. That is exactly what the group exists for, and it is
 * recorded here so the next reader does not "fix" the shared domain.
 *
 * **The token travels in the URL** — `https://{managerDomain}/auth-token?t={token}`. The service is
 * careful never to log it (*"a log line carrying it would be a working sign-in for anybody who can read
 * the logs"*) and then puts it in a query string, where it reaches browser history and the target's
 * access log. It is the contract the manager's `/auth-token` route already implements, so it is kept —
 * and said out loud rather than left for somebody to find.
 */

export interface FranchiseAdmin {
  id: number
  franchiseId: number
  systemRole: string
  active: boolean
  /**
   * The two fields the endpoint has always answered and this reader dropped - §3.7.
   *
   * `FranchiseAdminService.admins` puts `name` and `email` on every row, straight off `employees`.
   * Nothing read them because the only caller was the **jump** gate, which needs a count of active
   * `main_admin`s and no more. §3.7's panel has to name the person whose password is about to
   * change, so they are read now. Empty string rather than null: the two are the same thing on a
   * screen, and one fewer null is one fewer branch in every consumer.
   */
  name: string
  email: string
}

export function adminsOf(payload: unknown): FranchiseAdmin[] {
  const outer = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const rows = Array.isArray(outer.admins) ? outer.admins : Array.isArray(payload) ? payload : []
  return rows.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return []
    const admin = entry as Record<string, unknown>
    return [
      {
        id: typeof admin.id === 'number' ? admin.id : 0,
        franchiseId: typeof admin.franchiseId === 'number' ? admin.franchiseId : 0,
        systemRole: typeof admin.systemRole === 'string' ? admin.systemRole : '',
        active: admin.active === true,
        name: typeof admin.name === 'string' ? admin.name : '',
        email: typeof admin.email === 'string' ? admin.email : '',
      },
    ]
  })
}

/** Exactly what `issueToken` looks for: `system_role = 'main_admin' AND active = true`. */
export function hasJumpTarget(admins: readonly FranchiseAdmin[], franchiseId: number): boolean {
  return admins.some(
    (admin) => admin.franchiseId === franchiseId && admin.systemRole === 'main_admin' && admin.active,
  )
}

/** Why the jump is not offered, in the server's own terms, before the round trip that would say so. */
export function jumpBlockedNote(
  admins: readonly FranchiseAdmin[],
  franchiseId: number,
  managerDomain: string | null,
): string {
  if (!hasJumpTarget(admins, franchiseId)) {
    return tt('Žiadny aktívny main_admin pre túto franšízu. Najprv vytvorte admin účet.')
  }
  if (managerDomain === null || managerDomain.trim() === '') {
    return tt('Franšíza nemá nastavenú manažérsku doménu, takže nie je kam preskočiť.')
  }
  return ''
}

export function mayJump(
  admins: readonly FranchiseAdmin[],
  franchiseId: number,
  managerDomain: string | null,
): boolean {
  return jumpBlockedNote(admins, franchiseId, managerDomain) === ''
}

/** Said beside the button, because a token in a URL is worth knowing about before you open a tab. */
export function jumpWarning(): string {
  return tt('Odkaz obsahuje prihlasovací token - neposielajte ho ďalej.')
}

/** The redirect the server builds, read from its answer rather than assembled here. */
export function redirectUrlOf(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null) return null
  const url = (payload as Record<string, unknown>).redirectUrl
  if (typeof url !== 'string' || url.trim() === '') return null
  // Only ever an https jump to a manager domain. Anything else is not opened.
  try {
    return new URL(url).protocol === 'https:' ? url : null
  } catch {
    return null
  }
}

// ─── taking a franchise out of service ───────────────────────────────────────

/**
 * Deactivating and erasing are different, and the second is typed out.
 *
 * The same rule the manager's own platform pages already apply (FB-P01): a `PATCH` setting
 * `isActive: false` is reversible and a `DELETE` is not, so they do not get the same dialog. Erasing
 * asks for the slug in full — not a yes/no, because a yes/no is a reflex.
 */
export function mayDeactivate(isActive: boolean): boolean {
  return isActive
}

export function eraseConfirmed(typed: string, slug: string): boolean {
  return typed.trim() === slug && slug !== ''
}

/**
 * The WireGuard tunnel's own rules - SAAS.md §3.70, owner decision #30.
 *
 * **The owner:** *"Tunnel only, never the restore."* Three endpoints had no caller in the Tower; two
 * of them are wired by §3.70 and the restore deliberately is not.
 *
 * Kept out of the component for the reason the block above gives: what can be wrong here is the
 * decision, not the markup.
 */

/** Whether this franchise already has a peer. `hasWireguardConfig` alone is not enough - a row can
 * carry a config with no peer uuid after a half-finished removal, and the uuid is what the firewall
 * knows it by. */
export function tunnelProvisioned(franchise: {
  wireguardPeerUuid?: string | null
  hasWireguardConfig?: boolean
}): boolean {
  const uuid = (franchise.wireguardPeerUuid ?? '').trim()
  return uuid !== '' && franchise.hasWireguardConfig === true
}

/**
 * Whether the typed text releases the removal - §3.70.
 *
 * The same rule as `eraseConfirmed`, and deliberately the same *shape*: an operator who has typed a
 * slug to erase a franchise should not have to learn a second confirmation grammar for the tunnel.
 */
export function tunnelRemovalConfirmed(typed: string, slug: string): boolean {
  return typed.trim() === slug && slug !== ''
}

/**
 * What the removal is about to do, named.
 *
 * **The registry row is cleared whether or not the firewall agreed** - that is the endpoint's own
 * javadoc - so the sentence has to say what is lost rather than what is attempted. Legacy's
 * `confirm()` says *"Reštaurácia stratí prístup k VPN"* and that half is kept; what is added is
 * which franchise, because the Tower has several open at once in a way legacy's screen did not.
 */
export function tunnelRemovalPrompt(slug: string): string {
  return `${tt('Napíšte')} ${slug} ${tt('pre potvrdenie. Prevádzka stratí prístup k VPN a peer sa odstráni z OPNsense.')}`
}

/** The filename legacy downloads the config as, so a support folder keeps one naming. */
export function tunnelConfigFilename(slug: string): string {
  return `hk-${slug}.conf`
}

export function erasePrompt(slug: string): string {
  return `${tt('Napíšte')} ${slug} ${tt('pre potvrdenie. Franšízu ani jej databázu potom nemožno obnoviť.')}`
}

/**
 * The write half - SAAS.md §3.5, the owner's A14: *"there are no functionality at all.. and we need it
 * 1:1 except design."*
 *
 * FB-505 measured the Tower calling **3 of 44** write endpoints. This page had the erase confirmation
 * already - typing the slug out - and no call behind the button. These are the rules behind the calls,
 * kept out of the component because what can be wrong here is the *decision*, not the markup.
 */

/**
 * What a franchise's editable fields are, as `PATCH /super-admin/franchises/{id}` takes them.
 *
 * **Every key here is in the service's own `EDITABLE` map**, and that is not a style rule - a key
 * this interface carries and that map does not is a field the form throws away. `update` writes the
 * columns its patch map names and ignores the rest, so it answers 200 having done nothing, and the
 * refetch blanks whatever the operator typed. That was FB-630, for three fields, for months.
 *
 * The nine `billing*` are §3.53's queue item 2: accepted and **validated** by the server since
 * before this screen existed, rendered nowhere until §3.56.
 *
 * **And the inverse of the rule above is §3.87.** A key that map carries and this interface does not
 * is a field **nobody can set at all**, which is the worse direction because there is nothing on
 * screen to look wrong. Five were in that state: `displayName`, `logoUrl`, `faviconUrl`,
 * `primaryColor` and `secondaryColor` - the franchise's whole brand, which
 * `CentralRegistryService` serves to the customer app, where `branding.ts` writes `primaryColor`
 * over the theme as `--primary`.
 *
 * **And two more that the screen itself complains about.** `managerDomain` builds the jump URL, so
 * `jumpBlockedNote` said *"Franšíza nemá nastavenú manažérsku doménu, takže nie je kam preskočiť"* -
 * naming an unset field that nothing could set. `managerGroup` decides which branches a customer is
 * offered in the picker and gates the soft-launch switch through `visibleInGroup`. Seven columns in
 * all. `franchise-columns.test.ts` is the ratchet for both directions and it found these two after
 * the five, which is the argument for a ratchet over a careful reading.
 */
export interface FranchiseDraft {
  name: string
  slug: string
  displayName: string
  logoUrl: string
  faviconUrl: string
  primaryColor: string
  secondaryColor: string
  managerDomain: string
  managerGroup: string
  contactEmail: string
  contactPhone: string
  notes: string
  billingCompanyName: string
  billingAddress: string
  billingCity: string
  billingZip: string
  billingCountry: string
  billingIco: string
  billingDic: string
  billingIcDph: string
  billingEmail: string
}

export function draftOfFranchise(row: Record<string, unknown> | null): FranchiseDraft {
  const text = (value: unknown) => (typeof value === 'string' ? value : '')
  return {
    name: text(row?.name),
    slug: text(row?.slug),
    displayName: text(row?.displayName),
    logoUrl: text(row?.logoUrl),
    faviconUrl: text(row?.faviconUrl),
    primaryColor: text(row?.primaryColor),
    secondaryColor: text(row?.secondaryColor),
    managerDomain: text(row?.managerDomain),
    managerGroup: text(row?.managerGroup),
    contactEmail: text(row?.contactEmail),
    contactPhone: text(row?.contactPhone),
    notes: text(row?.notes),
    billingCompanyName: text(row?.billingCompanyName),
    billingAddress: text(row?.billingAddress),
    billingCity: text(row?.billingCity),
    billingZip: text(row?.billingZip),
    billingCountry: text(row?.billingCountry),
    billingIco: text(row?.billingIco),
    billingDic: text(row?.billingDic),
    billingIcDph: text(row?.billingIcDph),
    billingEmail: text(row?.billingEmail),
  }
}

/**
 * What a save may send.
 *
 * **Only the fields that changed**, because `update` writes the keys the body names and sending an
 * unchanged `slug` would rewrite the value a franchise's whole routing is keyed on for no reason.
 */
export function franchisePatch(
  draft: FranchiseDraft,
  original: FranchiseDraft,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {}
  for (const key of Object.keys(draft) as (keyof FranchiseDraft)[]) {
    if (draft[key].trim() !== original[key].trim()) {
      patch[key] = draft[key].trim()
    }
  }
  return patch
}

/**
 * Whether a draft is safe to send.
 *
 * A blank name would leave a tenant with no label on every screen that lists one, and a blank slug
 * would orphan its routing - `franchise_domains` and every `orders.type` query key off it.
 */
export function franchiseDraftProblem(draft: FranchiseDraft): string | null {
  if (draft.name.trim() === '') return tt('Názov je povinný')
  if (draft.slug.trim() === '') return tt('Slug je povinný')
  if (!/^[a-z0-9_-]+$/.test(draft.slug.trim())) {
    return tt('Slug môže obsahovať len a-z, 0-9, pomlčku a podčiarkovník')
  }
  if (draft.contactEmail.trim() !== '' && !draft.contactEmail.includes('@')) {
    return tt('Toto nie je e-mailová adresa')
  }
  if (draft.billingEmail.trim() !== '' && !draft.billingEmail.includes('@')) {
    return tt('Fakturačný e-mail nie je e-mailová adresa')
  }
  // ── The billing identity - §3.56 ────────────────────────────────────────────────────────────────
  //
  // **The server has always checked these**, in `FranchiseAdminService.requireBillingIdentityIsValid`
  // via `BusinessIdentityCheck`, so this is not a new rule and must not become a second definition
  // of one: the four calls below are `@agos/shared`'s, which is the same arithmetic the manager's
  // own billing page and the supplier screens use. Checking here only decides *where the operator
  // is told* - inline, before a request, instead of a 400 from a save they thought had worked.
  //
  // **The blank guard is this function's job, not the helpers'**, and getting that backwards is
  // what the first version of these four lines did - four tests failed on an *empty* identity,
  // which is the commonest state a franchise is in. `business-identity.ts` says so at the top:
  // *"Everything here reports; nothing refuses. A foreign supplier has no Slovak IČO and a record
  // legitimately starts empty."* `isValidIco('')` is therefore **false**, not exempt, and every
  // other caller writes the same `!== ''` - `supplier-identity.ts` included. The server reaches
  // the same answer by a different route, its `BusinessIdentifiers` exempting blank itself, so the
  // two agree on behaviour while disagreeing about where the exemption lives.
  if (draft.billingIco.trim() !== '' && !isValidIco(draft.billingIco)) return tt('Neplatné IČO')
  if (draft.billingDic.trim() !== '' && !isValidDic(draft.billingDic)) return tt('Neplatné DIČ')
  if (draft.billingIcDph.trim() !== '' && !isValidIcDph(draft.billingIcDph)) {
    return tt('Neplatné IČ DPH')
  }
  // **Both halves, always, and that is the whole subtlety of this rule.** The server compares the
  // *effective* pair - what the body carries falling back to what is stored - because comparing an
  // incoming IČ DPH against a DIČ the patch does not include hands the check a blank and its blank
  // exemption returns true. The draft here always holds both, changed or not, so passing them
  // straight through is the effective pair by construction.
  if (!icDphMatchesDic(draft.billingIcDph, draft.billingDic)) {
    return tt('IČ DPH nesúhlasí s DIČ - jedno z nich je preklep')
  }
  return null
}

/**
 * The two ways a franchise leaves service, and they are not the same call.
 *
 * `DELETE /super-admin/franchises/{id}` **deactivates**; `?force=true` removes the registry row. The
 * service's own javadoc is the thing worth repeating on the screen: *"the database survives either
 * way"*. So erasing a franchise here loses its **registration**, not its restaurant - and a screen
 * that let somebody believe otherwise would be the more dangerous mistake in both directions.
 */
export function retirementNote(force: boolean): string {
  return force
    ? tt('Odstráni registráciu a domény. Databáza franšízy zostáva na serveri.')
    : tt('Franšíza sa skryje a prestane fungovať. Dá sa znovu zapnúť.')
}

/**
 * What the franchise negotiated on top of its tier - `SAAS.md` §3.38.
 *
 * `GET /super-admin/franchises/{id}/package` answers the assignment row and the module overrides.
 * **Nothing called it**, which is what made the wipe below possible: with no stored row read, the
 * save had nothing to preserve.
 */
export interface TierAssignment {
  packageId: number | null
  /** `classic`, `modern-minimal` or `cozy-bistro`; `''` means *use the package's own theme*. */
  themeOverride: string
  /** What this franchise pays for its package instead of the list price. `''` means the list price. */
  monthlyPriceOverride: string
  notes: string
}

/** The three the customer app implements, down to the font stacks in `apps/customer/src/index.css`. */
export const THEME_CHOICES = ['classic', 'modern-minimal', 'cozy-bistro'] as const

export function assignmentOf(payload: unknown): TierAssignment {
  const row = (payload as { assignment?: Record<string, unknown> } | undefined)?.assignment
  // Narrowed rather than `String(value)`: `monthly_price_override` is `numeric`, which the server
  // sends as a string, but a caller handing us an object would otherwise reach the form as
  // `[object Object]` and be saved back. Lint catches exactly this.
  const text = (value: unknown): string => {
    if (typeof value === 'string') return value
    if (typeof value === 'number') return String(value)
    return ''
  }
  return {
    packageId: typeof row?.packageId === 'number' ? row.packageId : null,
    themeOverride: text(row?.themeOverride),
    monthlyPriceOverride: text(row?.monthlyPriceOverride),
    notes: text(row?.notes),
  }
}

/**
 * The body for `PUT .../package` - **all four fields, always** (§3.38).
 *
 * <b>This used to be `{ packageId }` and that was a silent wipe.</b> The endpoint upserts with
 * `theme_override = EXCLUDED.theme_override, monthly_price_override = EXCLUDED.monthly_price_override,
 * notes = EXCLUDED.notes`, so a body naming only the tier writes NULL over all three. Legacy avoids it
 * by having each of its two controls carry the other's stored value; AGOS never called the read that
 * would have supplied them.
 *
 * **So every caller passes the whole assignment**, with only the field it is changing replaced. A
 * partial body is not a partial update on this endpoint.
 *
 * **An empty theme is sent as `null` on purpose** - that is how *use the package's own theme* is
 * spelled, and `themeNameFor` falls back to the package when the column is null. An empty price is
 * `null` rather than `0`, because a franchise billed nothing is not the same as one on the list price.
 */
export function packagePayload(
  packageId: number | null,
  assignment?: TierAssignment,
): Record<string, unknown> {
  const blankToNull = (value: string): string | null => (value.trim() === '' ? null : value.trim())
  return {
    packageId,
    themeOverride: blankToNull(assignment?.themeOverride ?? ''),
    monthlyPriceOverride: blankToNull(assignment?.monthlyPriceOverride ?? ''),
    notes: blankToNull(assignment?.notes ?? ''),
  }
}

/** Whether anything in the negotiated block differs from what is stored. */
export function assignmentChanged(draft: TierAssignment, stored: TierAssignment): boolean {
  return (
    draft.themeOverride !== stored.themeOverride ||
    draft.monthlyPriceOverride.trim() !== stored.monthlyPriceOverride.trim() ||
    draft.notes !== stored.notes
  )
}

export function tierChanged(chosen: number | null, current: number | null): boolean {
  return (chosen ?? 0) !== (current ?? 0)
}

// ─── §3.7 - the negotiated exceptions, and the administrators ─────────────────

/**
 * What a franchise's module set is, layer by layer.
 *
 * `FranchiseModules.effectiveModuleKeys` is `always_on ∪ package_modules ∪ overrides`, where an
 * override with `granted = false` **removes** a module the package includes. So a module's presence
 * has four different causes and they look identical on a checkbox:
 *
 * | why it is on | can it be revoked |
 * |---|---|
 * | `always_on` | **no** - the union adds it before it reads anything else |
 * | the tier includes it | yes, with a `granted = false` override |
 * | an override grants it | yes, by removing the override |
 * | nothing | it is off, and an override can grant it |
 *
 * Which is why the control is a tri-state rather than a tick, and why the effective set is shown
 * beside it.
 */
export type OverrideState = 'inherit' | 'granted' | 'revoked'

export interface ModuleOverrideRow {
  key: string
  name: string
  category: string
  /** True when the platform grants it to everybody; then nothing here may change it. */
  alwaysOn: boolean
  /** True when the franchise's tier includes it. */
  inPackage: boolean
  state: OverrideState
}

function textOf(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/** The stored overrides as a map, last one winning - which is what the endpoint itself does. */
export function overridesByKey(payload: unknown): Map<string, boolean> {
  // Both shapes, because `GET …/effective-modules` wraps and a bare array is what a list endpoint
  // would answer. Narrowed by `Array.isArray` rather than asserted - the compiler already knows the
  // type after the guard, and the second cast was the one lint objected to.
  const wrapped = (payload as { overrides?: unknown } | undefined)?.overrides
  const rows: unknown[] = Array.isArray(payload)
    ? payload
    : Array.isArray(wrapped)
      ? wrapped
      : []
  const found = new Map<string, boolean>()
  for (const entry of rows) {
    const row = (typeof entry === 'object' && entry !== null ? entry : {}) as Record<string, unknown>
    const key = textOf(row.moduleKey)
    if (key === '') continue
    // `granted` absent means granted: the row exists to say *this franchise has it*, and only an
    // explicit false revokes. `FranchiseModules` reads it exactly that way.
    found.set(key, row.granted !== false)
  }
  return found
}

/**
 * One row per catalogue module, in the order the catalogue gives them.
 *
 * **Every module, not only the ones with an override.** A screen that listed only the exceptions
 * could show what had been negotiated and could not be used to negotiate one - which is the state
 * the Tower was in.
 */
/**
 * The module keys a tier grants, off `GET /super-admin/packages` - §3.189, FB-966.
 *
 * **Here rather than in the page, because the defect was unreachable from any test.** `FranchiseDetailPage`
 * read this in a `useMemo` as `Array.isArray(packages.data) ? packages.data : []`, and that endpoint answers
 * `Map.of("packages", …)` - an **object** - so the list was empty on every load for the life of the screen.
 * `overrideRows` below takes the answer as `inPackage` and `wouldHave` falls through to it, so every module in
 * `inherit` drew **`nemá`** and `· v balíčku` never rendered: measured, all 3 of the owner's franchises are on
 * `pizzamajstro`, which grants **209** modules.
 *
 * **Read off the writer, not the interface** - §3.122's rule, whose defect was a reader inventing a key, and
 * FB-647's, which is this same mistake on the read thirty lines above the one this fixes.
 *
 * **Both shapes accepted deliberately.** The sibling catalogue read already tolerates a bare array as well as
 * `{modules: […]}`, and a reader that insists on one shape is how this defect arrived; a bare array is also
 * what a later `packagesOf` refactor would hand it. **Nothing is inferred from the absence of the key** - no
 * tier, no keys, which is what `null` means here too.
 *
 * `null` for *no tier* answers an empty list rather than every key, because a franchise on no package is
 * granted nothing by one - the opposite default would mark every module as included for exactly the franchises
 * that have bought nothing.
 */
export function tierModuleKeys(payload: unknown, tierId: number | null): string[] {
  if (tierId === null) return []
  const body = (typeof payload === 'object' && payload !== null ? payload : {}) as {
    packages?: unknown
  }
  const rows = Array.isArray(payload) ? payload : Array.isArray(body.packages) ? body.packages : []
  const chosen = rows.find(
    (row) => typeof row === 'object' && row !== null && (row as { id?: unknown }).id === tierId,
  ) as { modules?: unknown } | undefined
  const keys = chosen === undefined ? [] : chosen.modules
  if (!Array.isArray(keys)) return []
  return keys.map((key) => textOf(key)).filter((key) => key !== '')
}

export function overrideRows(
  modules: readonly { key?: string; name?: string; category?: string; alwaysOn?: boolean }[],
  packageModules: readonly string[],
  stored: Map<string, boolean>,
): ModuleOverrideRow[] {
  const included = new Set(packageModules)
  return modules
    .filter((module) => textOf(module.key) !== '')
    .map((module) => {
      const key = textOf(module.key)
      const override = stored.get(key)
      return {
        key,
        name: textOf(module.name) === '' ? key : textOf(module.name),
        category: textOf(module.category) === '' ? 'other' : textOf(module.category),
        alwaysOn: module.alwaysOn === true,
        inPackage: included.has(key),
        state: override === undefined ? 'inherit' : override ? 'granted' : 'revoked',
      }
    })
}

/**
 * Whether this row's state may be changed.
 *
 * **`always_on` may not.** Six modules carry it; `effectiveModuleKeys` adds them before it looks at
 * the package or the overrides, so a row written for one would be stored and then ignored. §3.3
 * already refuses to toggle them in the tier picker, for the same reason and in the same words.
 */
export function mayOverride(row: ModuleOverrideRow): boolean {
  return !row.alwaysOn
}

/** Why a row cannot be changed, when it cannot. Empty when it can. */
export function overrideLockReason(row: ModuleOverrideRow): string {
  return row.alwaysOn ? tt('Platforma ho dáva všetkým - výnimku pre neho nastaviť nemožno.') : ''
}

/**
 * Whether the franchise has this module after all three layers.
 *
 * Derived rather than read, so the row can say what its own choice would produce before it is saved.
 * `GET …/effective-modules` answers the same question about what is *stored*, and the two are shown
 * together: one is the consequence of the pending choice, the other the current fact.
 */
export function wouldHave(row: ModuleOverrideRow): boolean {
  if (row.alwaysOn) return true
  if (row.state === 'granted') return true
  if (row.state === 'revoked') return false
  return row.inPackage
}

/**
 * The body `PUT …/{id}/overrides` takes - **the whole list, never a diff**.
 *
 * `replaceOverrides` deletes every row for the franchise and inserts what it is handed, so a partial
 * send silently revokes every exception it omits. That is the opposite of §3.5's franchise `PATCH`,
 * whose own test proves it sends a diff; these are two endpoints with two contracts and the screen
 * honours each.
 *
 * Rows in `inherit` are simply absent, which is what *follow the tier* means in this table.
 */
export function overridesPayload(rows: readonly ModuleOverrideRow[]): {
  overrides: { moduleKey: string; granted: boolean }[]
} {
  return {
    overrides: rows
      .filter((row) => !row.alwaysOn && row.state !== 'inherit')
      .map((row) => ({ moduleKey: row.key, granted: row.state === 'granted' })),
  }
}

/** Whether anything is left to save - so the button can be off rather than sending a no-op. */
export function overridesChanged(
  rows: readonly ModuleOverrideRow[],
  stored: Map<string, boolean>,
): boolean {
  const pending = overridesPayload(rows).overrides
  if (pending.length !== stored.size) return true
  return pending.some((row) => stored.get(row.moduleKey) !== row.granted)
}

/** How many exceptions this franchise has, for the heading - nought is a real and common answer. */
export function exceptionCount(rows: readonly ModuleOverrideRow[]): {
  granted: number
  revoked: number
} {
  return {
    granted: rows.filter((row) => !row.alwaysOn && row.state === 'granted').length,
    revoked: rows.filter((row) => !row.alwaysOn && row.state === 'revoked').length,
  }
}

/** The three things the panel can do to an administrator - §3.63 added the third. */
export type AdminAction = 'password' | 'deactivate' | 'activate'

/**
 * What changing an administrator actually does, said before it is done.
 *
 * `FranchiseAdminController`'s own comment is the thing worth putting on screen: *"a password change
 * or a deactivation takes effect on sessions that are already open."* A confirmation that only asked
 * *are you sure* would be withholding the one fact somebody needs.
 */
export function adminActionNote(action: AdminAction): string {
  if (action === 'password') {
    return tt('Nové heslo platí okamžite - otvorené prihlásenia tohto správcu prestanú fungovať.')
  }
  if (action === 'activate') {
    /**
     * **Not the mirror of the deactivation's note, and that is the point - §3.63, FB-648.**
     *
     * `updateAdmin` bumps `token_version` when it revokes and **never decrements it**, so nothing
     * that was signed out comes back: the administrator has to sign in again. A note written as the
     * opposite of the other one would promise that their open sessions resume, which is false.
     */
    return tt(
      'Správca sa bude môcť znova prihlásiť. Staré prihlásenia sa neobnovia - musí sa prihlásiť nanovo.',
    )
  }
  return tt(
    'Deaktivácia platí okamžite, aj na už otvorené prihlásenia. Správca zostáva v zázname - objednávky a zmeny sú písané na neho.',
  )
}

/**
 * What the button on an administrator's row does - §3.63, FB-648.
 *
 * **The panel used to offer a deactivation and no way back**: the button was
 * `disabled={!one.active}`, so a row badged *deaktivovaný* had nothing that could undo it and the
 * operator's only route was SQL. `PATCH .../admins/{id}` has always accepted `{active: true}` -
 * `updateAdmin`'s assignment list is built from whatever the body names and `active` is the first key
 * it looks for. Same shape as §3.11's one-way door, whose way back was also already on the wire.
 */
export function adminToggle(active: boolean): {
  action: Extract<AdminAction, 'activate' | 'deactivate'>
  label: string
  confirmLabel: string
  title: string
  danger: boolean
} {
  return active
    ? {
        action: 'deactivate',
        label: tt('Deaktivovať'),
        confirmLabel: tt('Deaktivovať'),
        title: tt('Deaktivovať správcu?'),
        danger: true,
      }
    : {
        action: 'activate',
        label: tt('Aktivovať'),
        confirmLabel: tt('Aktivovať'),
        title: tt('Aktivovať správcu?'),
        // Giving access back is not destructive, so it does not borrow the red dialog.
        danger: false,
      }
}

/** The body for either direction. One key, because that is all `updateAdmin` needs to be told. */
export function adminActivePatch(active: boolean): { active: boolean } {
  return { active }
}

/** A password long enough to be worth setting. The server's own floor, stated here first. */
export function passwordProblem(password: string): string {
  if (password.length === 0) return tt('Heslo je povinné')
  if (password.length < 8) return tt('Heslo musí mať aspoň 8 znakov')
  return ''
}

// ─── §3.13 - the soft-launch switch ───────────────────────────────────────────────────────────────

/**
 * The body that shows or hides a branch in its siblings' customer picker.
 *
 * **One key, and never the draft's.** `franchisePatch` sends a diff of the text form because
 * `update` writes whatever the body names; this is a different save with a different consequence -
 * it changes what customers see the moment it lands - so it carries nothing else. Folding it into
 * the form would put a visibility switch inside the *"3 changed fields"* counter beside a phone
 * number.
 */
export function softLaunchPatch(hidden: boolean): Record<string, unknown> {
  return { hiddenFromCustomerPicker: hidden }
}

/**
 * What the switch is about to do, said in the direction it is going.
 *
 * **A confirmation that reads the same either way is the defect this control exists to avoid.**
 * Showing a branch starts sending it real customers; hiding one does **not** take it offline - that
 * is `is_active`, six lines below on the same screen - and the fear that it does is the obvious one.
 */
export function softLaunchNote(hiding: boolean): string {
  return hiding
    ? tt('Prevádzka zmizne z výberu pobočiek u ostatných prevádzok. Zostáva plne dostupná na svojej vlastnej doméne - toto ju nevypína.')
    : tt('Zákazníci ostatných prevádzok ju začnú vidieť vo výbere pobočiek a môžu do nej objednávať.')
}

/** The state in words, which a switch position cannot carry. */
export function softLaunchState(hidden: boolean): string {
  return hidden
    ? tt('Skrytá pred zákazníkmi ostatných prevádzok - dostupná na vlastnej doméne')
    : tt('Ponúkaná zákazníkom ostatných prevádzok')
}

/**
 * Whether this branch may be hidden, given how many in its group are visible.
 *
 * **A group with every branch hidden answers an empty picker**, and the customer app's `gateAction`
 * then returns `reveal` on a site with nothing to reveal. Refused with the count rather than
 * producing a state the customer app has no sensible answer for.
 */
export function mayHide(visibleBranchesInGroup: number): boolean {
  return visibleBranchesInGroup > 1
}

export function hideRefusal(visibleBranchesInGroup: number): string {
  if (mayHide(visibleBranchesInGroup)) return ''
  return tt('Toto je posledná prevádzka, ktorú zákazníci vidia - skrytím by výber pobočiek zostal prázdny.')
}
